import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Quiz from "@/components/Quiz";
import LetterCard from "@/components/LetterCard";
import WordCard from "@/components/WordCard";
import ConfettiBurst from "@/components/ConfettiBurst";
import { buildVocabQuestions } from "@/lib/vocabQuiz";
import { makeStrictSequence } from "@/lib/quizSequence";
import { ConfirmDialog, ACTIONBAR_STYLES, shuffle } from "@/components/GroupUI";
import { ALPHABET_MODULES } from "@/lib/alphabetModules";
import { buildModuleQuestions } from "@/lib/alphabetQuiz";
import {
  Trash2,
  RotateCcw,
  ChevronDown,
  CheckSquare,
  Square,
  Play,
  Flame,
  Type,
  Languages,
  Layers,
  BookOpen,
  Infinity as InfinityIcon,
  Zap,
  X,
  ArrowLeft,
  ArrowRight,
  PartyPopper,
  Sparkles,
  Target,
  ListChecks,
  Volume2,
  Coins,
  Shuffle,
} from "lucide-react";
import { t } from "@/i18n";

/* Le backend peut renvoyer une clé d'erreur non-string (int, null, objet).
   Toute la page traite les clés comme du texte (split, includes, Set) :
   on normalise donc une bonne fois pour toutes ici. */
const asKey = (k) => (k == null ? "" : String(k));

/* Module d'appartenance d'une erreur, déduit du préfixe de clé —
   `alpha:ب` → lettres, `harakat:◌َ` → harakat, etc. Plus fiable que
   de deviner à partir du caractère : les signes du module 2 portent un
   cercle pointillé hors plage arabe, et les formes du module 3 des
   tatweel qui se ressemblent tous. */
const moduleOfKey = (key) => {
  const p = asKey(key).split(":")[0];
  if (p === "alpha") return "lettres";
  return ALPHABET_MODULES.some((m) => m.slug === p) ? p : null;
};

/* Doit rester aligné avec le backend : une erreur validée 3 fois de suite
   sort de la liste (progression.py). Vaut pour TOUS les types d'erreur —
   lettres, mots, leçons — pour que la règle affichée soit la vraie règle. */
const MASTER_STREAK = 3;
/* Doit rester égal à MINI_JEU_ERREURS dans dinar_economy.py côté backend.
   Le backend fait autorité sur le crédit réel : cette constante ne sert
   qu'à l'affichage, elle ne donne aucun Dinar à elle-même. */
const REWARD_ERRORS = 2;
/* Au-delà, une erreur est « tenace ». */
const TOUGH_AT = 3;

const SKILL_LABELS = {
  grammar: "myerrors.skill_grammar",
  vocabulary: "myerrors.skill_vocabulary",
  alphabet: "myerrors.skill_alphabet",
  conjugation: "myerrors.skill_conjugation",
  reading: "myerrors.skill_reading",
  writing: "myerrors.skill_writing",
};

/* Définition visuelle par famille */
const KIND_CONFIG = {
  alphabet: {
    labelKey: "myerrors.genre_lettres",
    icon: Type,
    color: "#B45309",
    card: "bg-amber-400/[0.09] border-amber-500/30",
    wash: "bg-amber-400/[0.06]",
    body: "bg-amber-400/[0.04]",
  },
  vocab: {
    labelKey: "myerrors.genre_mots",
    icon: Languages,
    color: "#4F46E5",
    card: "bg-indigo-400/[0.09] border-indigo-500/30",
    wash: "bg-indigo-400/[0.06]",
    body: "bg-indigo-400/[0.04]",
  },
  lesson: {
    labelKey: "myerrors.genre_lecons",
    icon: BookOpen,
    color: "#15803D",
    card: "bg-emerald-400/[0.09] border-emerald-500/30",
    wash: "bg-emerald-400/[0.06]",
    body: "bg-emerald-400/[0.04]",
  },
  skill: {
    labelKey: "myerrors.genre_autres",
    icon: Layers,
    color: "#B45309",
    card: "bg-secondary/60 border-border",
    wash: "bg-secondary/30",
    body: "bg-secondary/20",
  },
};

const toneOf = (kind) => {
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.skill;
  return {
    ...cfg,
    label: t(cfg.labelKey),
  };
};

const firstArabicChar = (s) =>
  (String(s || "").match(/[\u0600-\u06FF]/) || [])[0] || null;

const shuffleOptions = (q) => {
  const opts = q.options;
  if (!Array.isArray(opts) || opts.length < 2 || typeof q.answer !== "number")
    return q;
  const order = shuffle(opts.map((_, i) => i));
  const reorder = (arr) =>
    Array.isArray(arr) ? order.map((i) => arr[i]) : arr;
  return {
    ...q,
    options: order.map((i) => opts[i]),
    optionTranslations: reorder(q.optionTranslations),
    optionLatins: reorder(q.optionLatins),
    answer: order.indexOf(q.answer),
  };
};

const identityOfQuestion = (q) => {
  if (q.__eid) return q.__eid;
  if (q.char) return `char:${q.char}`;
  if (q.ref) return `word:${q.ref}`;
  return `key:${asKey(q.key ?? q.id)}`;
};

function TreeCard({
  title,
  metaBadge,
  progressPct,
  progressCaption,
  depth = 0,
  tone,
  open,
  onToggleOpen,
  checkboxState,
  onCheck,
  onPlay,
  children,
}) {
  return (
    <div
      className={`rounded-2xl overflow-hidden ${
        depth === 0
          ? `border-2 ${tone?.card || "bg-card"}`
          : "border bg-card/70"
      }`}
    >
      <div className="flex items-stretch">
        {checkboxState != null && (
          <button
            onClick={onCheck}
            aria-label={t("groupui.selectionner")}
            className="shrink-0 w-8 sm:w-11 grid place-items-center hover:bg-secondary/40 transition"
          >
            {checkboxState === "all" ? (
              <CheckSquare size={17} className="text-primary" />
            ) : checkboxState === "some" ? (
              <CheckSquare size={17} className="text-primary/50" />
            ) : (
              <Square size={17} className="text-muted-foreground" />
            )}
          </button>
        )}
        <button onClick={onToggleOpen} className="flex-1 min-w-0 text-left p-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
            <span className="font-bold text-sm leading-snug break-words [overflow-wrap:anywhere] line-clamp-2">
              {title}
            </span>
            {metaBadge && (
              <span className="self-start sm:shrink-0 text-[11px] font-medium text-muted-foreground bg-background/70 px-2 py-0.5 rounded-md whitespace-nowrap">
                {metaBadge}
              </span>
            )}
          </div>
          {progressPct != null && (
            <>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden mt-2">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {progressCaption && (
                <div className="text-[11px] text-muted-foreground mt-1">
                  {progressCaption}
                </div>
              )}
            </>
          )}
        </button>
        {onPlay && (
          <button
            onClick={onPlay}
            aria-label={t("groupui.jouer_ce_groupe")}
            className="shrink-0 w-8 sm:w-9 grid place-items-center border-l hover:bg-secondary/20 transition"
          >
            <Play size={13} fill="currentColor" className="text-primary" />
          </button>
        )}
        <button
          onClick={onToggleOpen}
          aria-label={open ? t("myerrors.replier") : t("groupui.deplier")}
          className="shrink-0 w-8 sm:w-9 grid place-items-center border-l hover:bg-secondary/20 transition"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {open && (
        <div
          className={
            depth === 0
              ? `border-t p-2 space-y-2 ${tone?.body || "bg-secondary/5"}`
              : "border-t p-2"
          }
        >
          {children}
        </div>
      )}
    </div>
  );
}

function ErrorsHero({ total, tough, kinds }) {
  return (
    <div className="rounded-3xl border bg-gradient-to-br from-card to-secondary/30 p-4 mb-4">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-head text-4xl font-extrabold leading-none tabular-nums">
            {total}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t("myerrors.nb_erreurs_a_revoir", {
              count: total,
              s: total > 1 ? "s" : "",
            })}
          </div>
        </div>
        {tough > 0 && (
          <div className="flex items-center gap-1.5 rounded-full bg-destructive/10 px-3 py-1.5 text-destructive">
            <Flame size={14} />
            <span className="text-xs font-bold tabular-nums">
              {tough} {t("myerrors.filtre_tenaces").toLowerCase()}
              {tough > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
      {total > 0 && (
        <>
          <div className="flex h-2 rounded-full overflow-hidden bg-secondary mb-2">
            {kinds.map(
              (k) =>
                k.count > 0 && (
                  <div
                    key={k.kind}
                    style={{
                      width: `${(k.count / total) * 100}%`,
                      backgroundColor: KIND_CONFIG[k.kind].color,
                    }}
                  />
                ),
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {kinds.map(
              (k) =>
                k.count > 0 && (
                  <span
                    key={k.kind}
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground"
                  >
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: KIND_CONFIG[k.kind].color }}
                    />
                    {t(KIND_CONFIG[k.kind].labelKey)}
                    <span className="font-bold tabular-nums">{k.count}</span>
                  </span>
                ),
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FilterChips({ filter, setFilter, counts }) {
  const chips = [
    { id: "all", label: t("myerrors.filtre_tout"), count: counts.all },
    {
      id: "tough",
      label: t("myerrors.filtre_tenaces"),
      count: counts.tough,
      icon: Flame,
    },
    {
      id: "alphabet",
      label: t("myerrors.genre_lettres"),
      count: counts.alphabet,
    },
    { id: "vocab", label: t("myerrors.genre_mots"), count: counts.vocab },
    { id: "lesson", label: t("myerrors.genre_lecons"), count: counts.lesson },
    { id: "skill", label: t("myerrors.genre_autres"), count: counts.skill },
  ].filter((c) => c.count > 0 || c.id === "all");

  return (
    <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3 -mx-4 px-4 scrollbar-none">
      {chips.map(({ id, label, count, icon: Icon }) => (
        <button
          key={id}
          onClick={() => setFilter(id)}
          className={`shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border-2 transition ${
            filter === id
              ? "border-primary bg-primary text-primary-foreground"
              : "border-transparent bg-secondary text-muted-foreground"
          }`}
        >
          {Icon && <Icon size={12} />}
          {label}
          <span className="tabular-nums opacity-70">{count}</span>
        </button>
      ))}
    </div>
  );
}

function LaunchSheet({
  pending,
  playMode,
  setPlayMode,
  count,
  setCount,
  order,
  setOrder,
  showLearn,
  setShowLearn,
  onStart,
  onClose,
}) {
  const total = pending?.errs.length || 0;
  const willPlay = pending?.respectCount ? Math.min(count, total) : total;
  return (
    <div
      className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border bg-card p-5 pb-8 sm:pb-5 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head text-lg font-bold">
            {t("myerrors.ta_seance")}
          </h3>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-secondary"
          >
            <X size={18} />
          </button>
        </div>

        <div className="rounded-2xl bg-secondary/50 p-4 mb-4 text-center">
          <div className="font-head text-3xl font-extrabold tabular-nums leading-none">
            {willPlay}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t("myerrors.myerrors.au_programme", {
              count: willPlay,
              s: willPlay > 1 ? "s" : "",
            })}
            {pending?.respectCount && total > willPlay
              ? t("myerrors.sur_nb_disponibles", { count: total })
              : ""}
          </div>
          {playMode === "infinite" && (
            <div className="text-[11px] text-primary font-semibold mt-1.5">
              {t("myerrors.en_boucle_jusqu_a")} {MASTER_STREAK}{" "}
              {t("myerrors.bonnes_reponses_chacune")}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
              {t("myerrors.mode_de_jeu")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                [
                  "classic",
                  t("MyErrors.Classique"),
                  Zap,
                  t("MyErrors.unpassage"),
                ],
                [
                  "infinite",
                  t("myerrors.mode_infini"),
                  InfinityIcon,
                  t("myerrors.jusqu_a_nb_coches", { count: MASTER_STREAK }),
                ],
              ].map(([id, label, Icon, sub]) => (
                <button
                  key={id}
                  onClick={() => setPlayMode(id)}
                  className={`rounded-xl border-2 p-2.5 text-center transition ${
                    playMode === id
                      ? "border-primary bg-primary/10"
                      : "hover:border-primary/40"
                  }`}
                >
                  <div className="flex items-center justify-center gap-1.5 text-sm font-semibold">
                    <Icon size={14} /> {label}
                  </div>
                  <div className="text-[10px] text-muted-foreground">{sub}</div>
                </button>
              ))}
            </div>
          </div>

          {pending?.respectCount && (
            <>
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">
                  {t("myerrors.nombre_d_erreurs")}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCount((c) => Math.max(5, c - 5))}
                    className="w-9 h-9 rounded-lg border grid place-items-center text-lg"
                  >
                    −
                  </button>
                  <span className="font-head text-base font-bold w-8 text-center tabular-nums">
                    {count}
                  </span>
                  <button
                    onClick={() => setCount((c) => c + 5)}
                    className="w-9 h-9 rounded-lg border grid place-items-center text-lg"
                  >
                    +
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">
                  {t("dashboard.priorite")}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ["tough", t("myerrors.priorite_tenaces")],
                    ["random", t("myerrors.priorite_hasard")],
                  ].map(([id, label]) => (
                    <button
                      key={id}
                      onClick={() => setOrder(id)}
                      className={`rounded-xl border-2 py-2.5 text-sm font-semibold transition ${
                        order === id
                          ? "border-primary bg-primary/10"
                          : "hover:border-primary/40"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <label className="flex items-center justify-between gap-3 cursor-pointer border-t pt-4">
            <span className="text-sm">
              <span className="font-medium">
                {t("myerrors.voir_les_fiches_avant")}
              </span>
              <span className="block text-[11px] text-muted-foreground">
                {t("myerrors.lettres_et_mots_de_la_seance")}
              </span>
            </span>
            <input
              type="checkbox"
              checked={showLearn}
              onChange={(e) => setShowLearn(e.target.checked)}
              className="w-5 h-5 accent-primary shrink-0"
            />
          </label>
        </div>

        <button
          onClick={onStart}
          className="w-full mt-5 py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2"
        >
          {playMode === "infinite" ? (
            <InfinityIcon size={17} />
          ) : (
            <Play size={16} fill="currentColor" />
          )}
          {t("myerrors.demarrer")}
        </button>
      </div>
    </div>
  );
}

function StreakDots({ box }) {
  return (
    <div className="flex gap-0.5 shrink-0">
      {Array.from({ length: MASTER_STREAK }).map((_, i) => (
        <span
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${
            i < box ? "bg-emerald-500" : "bg-secondary"
          }`}
        />
      ))}
    </div>
  );
}

function ToughBadge({ fails }) {
  if ((fails || 0) < TOUGH_AT) return null;
  return (
    <span className="flex items-center gap-0.5 text-[10px] font-bold text-destructive shrink-0 tabular-nums">
      <Flame size={11} />
      {fails}
    </span>
  );
}

function DeleteButton({ onClick }) {
  return (
    <button
      onClick={(ev) => {
        ev.stopPropagation();
        onClick();
      }}
      className="shrink-0 p-2 -m-1 rounded-lg text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition"
      aria-label={t("myerrors.ignorer_cette_erreur")}
    >
      <Trash2 size={14} />
    </button>
  );
}

function AlphabetLetterRow({
  ch,
  entries,
  selectedKeys,
  onToggleKeys,
  onAudio,
  letterByChar,
}) {
  const keys = entries.map((e) => e.key);
  const sel = keys.every((k) => selectedKeys.has(k));
  const fails = Math.max(...entries.map((e) => e.fails || 0));
  const box = Math.max(...entries.map((e) => e.box || 0));
  const l = letterByChar[ch];
  return (
    <div
      onClick={() => onToggleKeys(keys, !sel)}
      className={`flex items-center gap-2.5 rounded-xl p-2.5 cursor-pointer transition ${
        sel ? "bg-primary/10" : "hover:bg-secondary/40"
      }`}
    >
      {sel ? (
        <CheckSquare size={16} className="text-primary shrink-0" />
      ) : (
        <Square size={16} className="text-muted-foreground shrink-0" />
      )}
      <span
        className="arabic text-3xl leading-none w-10 text-center shrink-0"
        dir="rtl"
      >
        {ch}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold leading-tight break-words [overflow-wrap:anywhere]">
          {l?.name || "—"}
        </div>
        <div className="text-[11px] text-muted-foreground leading-snug break-words [overflow-wrap:anywhere] line-clamp-2">
          {t("myerrors.se_prononce")} {l?.sound || l?.translit || "?"} »
        </div>
      </div>
      <button
        onClick={(ev) => {
          ev.stopPropagation();
          onAudio("letter", ch);
        }}
        className="shrink-0 w-8 h-8 rounded-full border grid place-items-center hover:bg-secondary transition"
        aria-label={t("lettercard.ecouter_la_lettre")}
      >
        <Volume2 size={13} />
      </button>
      <ToughBadge fails={fails} />
      <StreakDots box={box} />
    </div>
  );
}

function VocabWordRow({
  e,
  selectedKeys,
  onToggleKeys,
  wordById,
  wordArIndex,
  onDeleteOne,
}) {
  const wid =
    (e.ref && wordById[e.ref] && e.ref) ||
    wordArIndex[String(e.item || "").trim()];
  const w = wid ? wordById[wid] : null;
  const sel = selectedKeys.has(e.key);
  return (
    <div
      onClick={() => onToggleKeys([e.key], !sel)}
      className={`flex items-center gap-2.5 rounded-xl p-2.5 cursor-pointer transition ${
        sel ? "bg-primary/10" : "hover:bg-secondary/40"
      }`}
    >
      {sel ? (
        <CheckSquare size={16} className="text-primary shrink-0" />
      ) : (
        <Square size={16} className="text-muted-foreground shrink-0" />
      )}
      <span
        className="arabic text-2xl leading-none min-w-10 text-center shrink-0"
        dir="rtl"
      >
        {w ? w.word_ar : e.item || "—"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold leading-tight break-words [overflow-wrap:anywhere]">
          {w ? w.translation_en : e.correct || "—"}
        </div>
        <div className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
          {w?.translit
            ? t("myerrors.se_prononce_entre_guillemets", {
                translit: w.translit,
              })
            : t("myerrors.vu_nb_fois", { count: w?.seen ?? 0 })}
        </div>
      </div>
      <ToughBadge fails={e.fails} />
      <StreakDots box={e.box || 0} />
      <DeleteButton onClick={() => onDeleteOne(e)} />
    </div>
  );
}

function SimpleErrorRow({ e, q, selectedKeys, onToggleKeys, onDeleteOne }) {
  const sel = selectedKeys.has(e.key);
  const label = e.item || q?.prompt || q?.question || "—";
  const answer = e.correct || q?.answer || q?.correct || "—";
  return (
    <div
      onClick={() => onToggleKeys([e.key], !sel)}
      className={`flex items-start gap-2.5 rounded-xl px-2.5 py-2 cursor-pointer transition ${
        sel ? "bg-primary/10" : "hover:bg-secondary/50"
      }`}
    >
      {sel ? (
        <CheckSquare size={16} className="text-primary shrink-0 mt-0.5" />
      ) : (
        <Square size={16} className="text-muted-foreground shrink-0 mt-0.5" />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium leading-snug line-clamp-2">
          {label}
        </div>
        <div className="text-[11px] text-muted-foreground truncate">
          {t("myerrors.reponse")} {answer}
        </div>
      </div>
      <ToughBadge fails={e.fails} />
      <StreakDots box={e.box || 0} />
      <DeleteButton onClick={() => onDeleteOne(e)} />
    </div>
  );
}

function AlphabetGroupBody({
  errors,
  selectedKeys,
  onToggleKeys,
  onAudio,
  letterByChar,
}) {
  const byChar = {};
  for (const e of errors) {
    const ch =
      e.char ||
      (moduleOfKey(e.key) && asKey(e.key).split(":").pop()) ||
      firstArabicChar(e.item) ||
      firstArabicChar(e.correct);
    if (!ch) continue;
    (byChar[ch] = byChar[ch] || []).push(e);
  }
  const chars = Object.keys(byChar).sort(
    (a, b) =>
      Math.max(...byChar[b].map((e) => e.fails || 0)) -
      Math.max(...byChar[a].map((e) => e.fails || 0)),
  );
  return (
    <div className="space-y-1 max-h-96 overflow-y-auto pr-0.5">
      {chars.map((ch) => (
        <AlphabetLetterRow
          key={ch}
          ch={ch}
          entries={byChar[ch]}
          selectedKeys={selectedKeys}
          onToggleKeys={onToggleKeys}
          onAudio={onAudio}
          letterByChar={letterByChar}
        />
      ))}
    </div>
  );
}

function VocabGroupBody({
  errors,
  selectedKeys,
  onToggleKeys,
  wordById,
  wordArIndex,
  onDeleteOne,
}) {
  const sorted = [...errors].sort(
    (a, b) => (b.fails || 0) - (a.fails || 0) || (a.box || 0) - (b.box || 0),
  );
  return (
    <div className="space-y-1 max-h-96 overflow-y-auto pr-0.5">
      {sorted.map((e) => (
        <VocabWordRow
          key={e.key}
          e={e}
          selectedKeys={selectedKeys}
          onToggleKeys={onToggleKeys}
          wordById={wordById}
          wordArIndex={wordArIndex}
          onDeleteOne={onDeleteOne}
        />
      ))}
    </div>
  );
}

function SimpleGroupBody({
  errors,
  selectedKeys,
  onToggleKeys,
  onDeleteOne,
  reviewByKey,
}) {
  return (
    <div className="space-y-1 max-h-80 overflow-y-auto pr-0.5">
      {errors.map((e) => (
        <SimpleErrorRow
          key={e.key}
          e={e}
          q={reviewByKey?.[e.key]}
          selectedKeys={selectedKeys}
          onToggleKeys={onToggleKeys}
          onDeleteOne={onDeleteOne}
        />
      ))}
    </div>
  );
}

function prettifyLessonId(s) {
  if (!s) return "—";
  const str = asKey(s);
  return str.replace(/_/g, " ").charAt(0).toUpperCase() + str.slice(1);
}

const PANE_STYLES = `
  .hf-pane {
    position: fixed;
    top: var(--nav-top-mobile, 4rem);
    bottom: var(--nav-bottom-mobile, 4rem);
    left: 0;
    right: 0;
    z-index: 30;
    display: flex;
    justify-content: center;
    padding: 0.5rem 0.75rem;
    overflow: hidden;
    overscroll-behavior: contain;
  }
  @media (min-width: 768px) {
    .hf-pane {
      top: var(--nav-top-desktop, 5.5rem);
      bottom: 1.25rem;
      padding: 0 clamp(1rem, 2.5vw, 2rem);
    }
  }
  .hf-card {
    width: 100%;
    max-width: 32rem;
    height: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 1.25rem;
  }
`;

function Pane({ tone, children }) {
  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    return () => {
      body.style.overflow = prevOverflow;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, []);

  return (
    <>
      <style>{PANE_STYLES}</style>
      <div className="hf-pane bg-background md:bg-transparent">
        <div className={`hf-card border ${tone?.wash || "bg-card"}`}>
          {children}
        </div>
      </div>
    </>
  );
}

function LearnDeck({ kind, title, cards, onAudio, onDone, onExit }) {
  const [i, setI] = useState(0);
  const tone = toneOf(kind);
  const total = cards.length;
  const last = i >= total - 1;
  useEffect(() => setI(0), [cards]);
  if (!total) return null;
  return (
    <Pane tone={tone}>
      <div className="shrink-0 px-4 pt-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onExit}
            className="rounded-full p-1.5 hover:bg-secondary"
            aria-label={t("alphabetlearnscreen.quitter")}
          >
            <X size={18} />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold truncate">{title}</div>
            <div className="text-[11px] text-muted-foreground tabular-nums">
              {t("myerrors.fiche_i_sur_total", { i: i + 1, total })}
            </div>
          </div>
        </div>
        <div className="h-1.5 rounded-full bg-secondary overflow-hidden mt-2">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{ width: `${((i + 1) / total) * 100}%` }}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        {kind === "alphabet" ? (
          <LetterCard letter={cards[i]} onAudio={onAudio} />
        ) : (
          <WordCard word={cards[i]} />
        )}
      </div>

      <div className="shrink-0 border-t bg-card/85 backdrop-blur px-3 py-2.5 flex items-center gap-2">
        <button
          onClick={() => setI((n) => Math.max(0, n - 1))}
          disabled={i === 0}
          className="shrink-0 w-11 py-3 rounded-2xl border grid place-items-center disabled:opacity-40"
          aria-label={t("myerrors.fiche_precedente")}
        >
          <ArrowLeft size={16} />
        </button>
        {last ? (
          <button
            onClick={onDone}
            className="flex-1 py-3 rounded-2xl bg-primary text-primary-foreground font-bold"
          >
            {t("dictionary.commencer")}
          </button>
        ) : (
          <button
            onClick={() => setI((n) => Math.min(total - 1, n + 1))}
            className="flex-1 py-3 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2"
          >
            {t("myerrors.suivante")} <ArrowRight size={16} />
          </button>
        )}
        <button
          onClick={onDone}
          className="shrink-0 px-3 py-3 rounded-2xl border text-sm font-semibold"
        >
          {t("tutorial.passer")}
        </button>
      </div>
    </Pane>
  );
}

function SummaryShell({ tone, children, footer }) {
  return (
    <Pane tone={tone}>
      <div className="flex-1 min-h-0 px-3 pt-3 pb-2 flex flex-col gap-2.5 overflow-hidden">
        {children}
      </div>
      <div className="shrink-0 border-t bg-card/85 backdrop-blur px-3 py-2.5 space-y-2">
        {footer}
      </div>
    </Pane>
  );
}

function ProgressList({ items }) {
  const [expanded, setExpanded] = useState(false);
  const sorted = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          b.box - a.box || String(a.label).localeCompare(String(b.label)),
      ),
    [items],
  );
  if (!sorted.length) return null;
  const COLLAPSED = 5;
  const shown = expanded ? sorted : sorted.slice(0, COLLAPSED);
  const hidden = sorted.length - shown.length;
  const doneCount = sorted.filter((it) => it.box >= MASTER_STREAK).length;

  return (
    <div className="flex-1 min-h-0 flex flex-col rounded-2xl border bg-card/70 overflow-hidden">
      <div className="shrink-0 flex items-center justify-between px-3 py-2 border-b">
        <span className="text-xs font-bold">
          {t("myerrors.ta_progression")}
        </span>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {doneCount}/{sorted.length} {t("myerrors.maitrises")}
        </span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-1.5 space-y-0.5">
        {shown.map((it) => (
          <div
            key={it.id}
            className={`flex items-center gap-2.5 rounded-xl px-2 py-1.5 ${
              it.box >= MASTER_STREAK ? "bg-emerald-500/10" : ""
            }`}
          >
            {it.ar ? (
              <span
                className="arabic text-xl leading-none min-w-8 text-center shrink-0"
                dir="rtl"
              >
                {it.ar}
              </span>
            ) : (
              <span className="w-8 shrink-0 grid place-items-center text-muted-foreground">
                <ListChecks size={13} />
              </span>
            )}
            <span className="min-w-0 flex-1 text-xs leading-snug line-clamp-1">
              {it.label}
            </span>
            <StreakDots box={it.box} />
            <span className="text-[10px] text-muted-foreground tabular-nums w-7 text-right">
              {Math.min(it.box, MASTER_STREAK)}/{MASTER_STREAK}
            </span>
          </div>
        ))}
      </div>
      {hidden > 0 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="shrink-0 border-t py-2 text-[11px] font-semibold text-primary flex items-center justify-center gap-1"
        >
          {t("myerrors.voir_les_nb_autres", { count: hidden })}{" "}
          <ChevronDown size={13} />
        </button>
      )}
      {expanded && (
        <button
          onClick={() => setExpanded(false)}
          className="shrink-0 border-t py-2 text-[11px] font-semibold text-muted-foreground flex items-center justify-center gap-1"
        >
          {t("myerrors.replier")}{" "}
          <ChevronDown size={13} className="rotate-180" />
        </button>
      )}
    </div>
  );
}

export default function MyErrors() {
  const navigate = useNavigate();

  const [errors, setErrors] = useState(null);
  const [alphaGroups, setAlphaGroups] = useState([]);
  const [vocabGroups, setVocabGroups] = useState([]);
  const [wordById, setWordById] = useState({});
  const [wordGroupOf, setWordGroupOf] = useState({});
  const [letterByChar, setLetterByChar] = useState({});
  const [charGroupOf, setCharGroupOf] = useState({});
  const [lessonMap, setLessonMap] = useState({});
  const [lessonThemeOf, setLessonThemeOf] = useState({});
  const [reviewByKey, setReviewByKey] = useState({});

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [openMap, setOpenMap] = useState({});
  const [filter, setFilter] = useState("all");
  const [showHelp, setShowHelp] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const [playMode, setPlayMode] = useState("classic");
  const [count, setCount] = useState(10);
  const [order, setOrder] = useState("tough");
  const [showLearn, setShowLearn] = useState(true);
  const [pending, setPending] = useState(null);

  const [plan, setPlan] = useState(null);
  const [learnPending, setLearnPending] = useState(false);
  const [roundBreak, setRoundBreak] = useState(null);
  const [done, setDone] = useState(null);
  const [starting, setStarting] = useState(false);
  const [notice, setNotice] = useState("");
  const [hint, setHint] = useState(null);
  const [confirmExit, setConfirmExit] = useState(false);

  const [progress, setProgress] = useState({});
  const progressRef = useRef({});
  const setProgressBoth = useCallback((next) => {
    progressRef.current = next;
    setProgress(next);
  }, []);

  const load = useCallback(async () => {
    const [errRes, themesRes, alphaRes, vgRes, revRes] = await Promise.all([
      api.get("/progress/errors").catch(() => ({ data: [] })),
      api.get("/content/themes").catch(() => ({ data: [] })),
      api.get("/content/alphabet").catch(() => ({ data: {} })),
      api.get("/progression/vocab-groups").catch(() => ({ data: {} })),
      api
        .get("/progress/review?scope=errors&limit=1000")
        .catch(() => ({ data: {} })),
    ]);

    const rawErrors = Array.isArray(errRes.data) ? errRes.data : [];
    setErrors(
      rawErrors
        .filter((e) => e && typeof e === "object")
        .map((e) => ({ ...e, key: asKey(e.key), rawKey: e.key })),
    );

    const qmap = {};
    for (const q of revRes.data?.questions || []) {
      if (q.key != null) qmap[asKey(q.key)] = q;
      if (q.id != null) qmap[asKey(q.id)] = q;
    }
    setReviewByKey(qmap);

    const lmap = {};
    const tmap = {};
    (themesRes.data || []).forEach((th) =>
      (th.lessons || []).forEach((l) => {
        lmap[asKey(l.id)] = l.title;
        tmap[asKey(l.id)] = th.title;
      }),
    );
    setLessonMap(lmap);
    setLessonThemeOf(tmap);

    const ag = alphaRes.data?.groups || [];
    const lbc = {};
    const cgo = {};
    for (const g of ag)
      for (const l of g.letter_details || []) {
        lbc[l.char] = l;
        cgo[l.char] = g.id;
      }
    const staticGroups = [];
    for (const m of ALPHABET_MODULES) {
      if (m.remote) continue;
      for (const g of m.groups || []) {
        staticGroups.push({ ...g, module: m.slug, moduleTitle: m.title });
        for (const l of g.letter_details || []) {
          lbc[l.char] = l;
          cgo[l.char] = g.id;
        }
      }
    }
    setAlphaGroups([
      ...ag.map((g) => ({
        ...g,
        module: "lettres",
        moduleTitle: t("myerrors.lettres_de_base"),
      })),
      ...staticGroups,
    ]);
    setLetterByChar(lbc);
    setCharGroupOf(cgo);

    const vg = vgRes.data?.groups || [];
    setVocabGroups(vg);
    const wordsRes = await Promise.all(
      vg.map((g) =>
        api
          .get(`/progression/vocab-groups/${g.id}/words`)
          .catch(() => ({ data: { words: [] } })),
      ),
    );
    const wbi = {};
    const wgo = {};
    vg.forEach((g, i) => {
      (wordsRes[i].data?.words || []).forEach((w) => {
        wbi[w.id] = w;
        wgo[w.id] = g.id;
      });
    });
    setWordById(wbi);
    setWordGroupOf(wgo);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const playAudio = (kind, char) => {
    const a = new Audio(
      `/api/content/alphabet/audio/${kind}/${encodeURIComponent(char)}`,
    );
    a.play().catch(() => {});
  };

  const wordArIndex = useMemo(() => {
    const m = {};
    for (const id of Object.keys(wordById)) {
      const w = wordById[id];
      if (w.word_ar) m[String(w.word_ar).trim()] = id;
    }
    return m;
  }, [wordById]);

  const resolveLessonId = useCallback(
    (raw) => {
      if (raw == null || raw === "") return null;
      const LESSON_PREFIX = /^(lesson|leçon|lecon|cours|course|quiz)[:_\-]/i;
      const s = asKey(raw).split("#q")[0].replace(LESSON_PREFIX, "");
      if (lessonMap[s]) return s;
      const hit = Object.keys(lessonMap).find(
        (k) => k === s || k.endsWith(s) || s.endsWith(k),
      );
      return hit || null;
    },
    [lessonMap],
  );

  const classify = useCallback(
    (e) => {
      const key = asKey(e.key);
      if (e.skill === "lesson_quiz" || key.includes("#q") || e.lesson_id)
        return { kind: "lesson" };

      const wid =
        (e.ref && wordById[e.ref] && e.ref) ||
        wordArIndex[String(e.item || "").trim()] ||
        wordArIndex[String(e.correct || "").trim()] ||
        null;
      if (wid)
        return {
          kind: "vocab",
          groupId: wordGroupOf[wid] || null,
          wordId: wid,
        };
      if (e.skill === "vocabulary")
        return { kind: "vocab", groupId: null, wordId: null };

      const mod = moduleOfKey(key);
      const ch =
        e.char ||
        (mod && key.split(":").pop()) ||
        firstArabicChar(e.item) ||
        firstArabicChar(e.correct);
      if (mod || e.skill === "alphabet" || (ch && charGroupOf[ch]))
        return {
          kind: "alphabet",
          groupId: ch ? charGroupOf[ch] || null : null,
          module: mod,
          char: ch,
        };

      return { kind: "skill", groupId: e.skill || "grammar" };
    },
    [wordById, wordArIndex, wordGroupOf, charGroupOf],
  );

  const identityOfError = useCallback(
    (e) => {
      const c = classify(e);
      if (c.kind === "alphabet" && c.char) return `char:${c.char}`;
      if (c.kind === "vocab" && c.wordId) return `word:${c.wordId}`;
      return `key:${asKey(e.key)}`;
    },
    [classify],
  );

  const allTagged = useMemo(
    () => (errors || []).map((e) => ({ e, c: classify(e) })),
    [errors, classify],
  );

  const counts = useMemo(() => {
    const c = {
      all: allTagged.length,
      tough: 0,
      alphabet: 0,
      vocab: 0,
      lesson: 0,
      skill: 0,
    };
    for (const { e, c: cl } of allTagged) {
      if ((e.fails || 0) >= TOUGH_AT) c.tough++;
      c[cl.kind]++;
    }
    return c;
  }, [allTagged]);

  const kindsBreakdown = useMemo(
    () =>
      ["alphabet", "vocab", "lesson", "skill"].map((kind) => ({
        kind,
        count: counts[kind],
      })),
    [counts],
  );

  const tagged = useMemo(() => {
    if (filter === "all") return allTagged;
    if (filter === "tough")
      return allTagged.filter(({ e }) => (e.fails || 0) >= TOUGH_AT);
    return allTagged.filter(({ c }) => c.kind === filter);
  }, [allTagged, filter]);

  const visibleErrors = useMemo(() => tagged.map((t) => t.e), [tagged]);

  const sections = useMemo(() => {
    const alphaMods = {};
    const vocabCats = {};
    const skillGroups = {};
    const lessonThemes = {};

    for (const { e, c } of tagged) {
      if (c.kind === "alphabet") {
        const modSlug = c.module || "lettres";
        const modTitle =
          ALPHABET_MODULES.find((m) => m.slug === modSlug)?.title ||
          t("myerrors.lettres");
        const gid = c.groupId || "misc";
        if (!alphaMods[modSlug])
          alphaMods[modSlug] = {
            id: `cat:alpha:${modSlug}`,
            title: modTitle,
            subs: {},
          };
        if (!alphaMods[modSlug].subs[gid]) {
          const g = alphaGroups.find((x) => x.id === c.groupId);
          alphaMods[modSlug].subs[gid] = {
            id: `alpha:${modSlug}:${gid}`,
            title: g ? g.label : t("myerrors.autres"),
            allChars: g ? (g.letter_details || []).map((l) => l.char) : [],
            errors: [],
          };
        }
        alphaMods[modSlug].subs[gid].errors.push(e);
      } else if (c.kind === "vocab") {
        const g = vocabGroups.find((x) => x.id === c.groupId);
        const catName = g?.category || t("myerrors.general");
        const subId = c.groupId || "misc";
        if (!vocabCats[catName])
          vocabCats[catName] = {
            id: `cat:vocab:${catName}`,
            title: catName,
            subs: {},
          };
        if (!vocabCats[catName].subs[subId]) {
          vocabCats[catName].subs[subId] = {
            id: `vocab:${catName}:${subId}`,
            title: g ? g.sous_categorie || g.title : t("myerrors.autres_mots"),
            pct: g ? g.pct : null,
            errors: [],
          };
        }
        vocabCats[catName].subs[subId].errors.push(e);
      } else if (c.kind === "lesson") {
        const key = asKey(e.key);
        const rawLid =
          e.ref ||
          e.lesson_id ||
          (key.includes("#q") ? key.split("#q")[0] : key);
        const lid = resolveLessonId(rawLid);
        const title = lid ? lessonMap[lid] : prettifyLessonId(rawLid);
        const themeTitle =
          (lid && lessonThemeOf[lid]) || t("myerrors.autres_themes");
        if (!lessonThemes[themeTitle])
          lessonThemes[themeTitle] = {
            id: `cat:lesson:${themeTitle}`,
            title: themeTitle,
            subs: {},
          };
        const subId = lid || title;
        if (!lessonThemes[themeTitle].subs[subId])
          lessonThemes[themeTitle].subs[subId] = {
            id: `lesson:${themeTitle}:${subId}`,
            title,
            errors: [],
          };
        lessonThemes[themeTitle].subs[subId].errors.push(e);
      } else {
        const gid = c.groupId;
        if (!skillGroups[gid])
          skillGroups[gid] = {
            id: `skill:${gid}`,
            title: SKILL_LABELS[gid] ? t(SKILL_LABELS[gid]) : gid,
            errors: [],
          };
        skillGroups[gid].errors.push(e);
      }
    }

    const byErrCountDesc = (a, b) => b.errors.length - a.errors.length;
    const catByErrCountDesc = (a, b) =>
      b.subs.reduce((n, s) => n + s.errors.length, 0) -
      a.subs.reduce((n, s) => n + s.errors.length, 0);
    const finishCats = (cats) =>
      Object.values(cats)
        .map((cat) => ({
          ...cat,
          subs: Object.values(cat.subs).sort(byErrCountDesc),
        }))
        .sort(catByErrCountDesc);

    const out = [];
    if (Object.keys(alphaMods).length)
      out.push({
        id: "type:alphabet",
        kind: "alphabet",
        title: t(KIND_CONFIG.alphabet.labelKey),
        categories: finishCats(alphaMods),
      });
    if (Object.keys(vocabCats).length)
      out.push({
        id: "type:vocab",
        kind: "vocab",
        title: t(KIND_CONFIG.vocab.labelKey),
        categories: finishCats(vocabCats),
      });
    if (Object.keys(lessonThemes).length)
      out.push({
        id: "type:lesson",
        kind: "lesson",
        title: t(KIND_CONFIG.lesson.labelKey),
        categories: finishCats(lessonThemes),
      });
    if (Object.keys(skillGroups).length)
      out.push({
        id: "type:skill",
        kind: "skill",
        title: t(KIND_CONFIG.skill.labelKey),
        groups: Object.values(skillGroups).sort(byErrCountDesc),
      });
    return out;
  }, [
    tagged,
    alphaGroups,
    vocabGroups,
    lessonMap,
    lessonThemeOf,
    resolveLessonId,
  ]);

  const toggleKeys = useCallback(
    (keys, value) =>
      setSelectedKeys((prev) => {
        const n = new Set(prev);
        keys.forEach((k) => (value ? n.add(asKey(k)) : n.delete(asKey(k))));
        return n;
      }),
    [],
  );

  const toggleOpen = useCallback(
    (id) => setOpenMap((p) => ({ ...p, [id]: !p[id] })),
    [],
  );

  const checkboxStateFor = (keys, selected) => {
    if (!keys.length) return "none";
    const n = keys.filter((k) => selected.has(asKey(k))).length;
    return n === 0 ? "none" : n === keys.length ? "all" : "some";
  };

  const deleteOne = useCallback((err) => {
    setConfirm({
      title: t("myerrors.ignorer_cette_erreur_titre"),
      message: t("myerrors.sera_retiree_de_ta_liste", {
        item: err.item || err.key,
      }),
      confirmLabel: t("myerrors.supprimer"),
      onConfirm: async () => {
        setConfirm(null);
        await api.delete("/progress/errors", {
          data: { key: err.rawKey !== undefined ? err.rawKey : err.key },
        });
        setErrors((prev) => prev.filter((e) => e.key !== err.key));
        setSelectedKeys((prev) => {
          const n = new Set(prev);
          n.delete(err.key);
          return n;
        });
      },
    });
  }, []);

  const cleanAlphabet = (qs) =>
    qs.map((q) => {
      const s = { ...q, skill: "alphabet", char: q.char };
      s.key = q.key || (q.char ? `alpha:${q.char}` : undefined);
      s.__eid = q.char ? `char:${q.char}` : `key:${asKey(s.key)}`;
      delete s.subtext;
      delete s.subtitle;
      delete s.hint;
      delete s.help;
      delete s.translit;
      delete s.name;
      delete s.sub;
      return s;
    });

  const buildAlphabetPart = async (errs) => {
    const byMod = {};
    for (const e of errs) {
      const key = asKey(e.key);
      const mod = moduleOfKey(key) || "lettres";
      const ch =
        e.char ||
        (moduleOfKey(key) && key.split(":").pop()) ||
        firstArabicChar(e.item);
      if (ch) (byMod[mod] = byMod[mod] || new Set()).add(ch);
    }
    const parts = [];
    const remoteChars = [...(byMod.lettres || [])];
    if (remoteChars.length) {
      const gids = [
        ...new Set(remoteChars.map((c) => charGroupOf[c]).filter(Boolean)),
      ];
      const n = Math.max(30, remoteChars.length * 10);
      const q = `count=${n}${gids.length ? `&groups=${gids.join(",")}` : ""}`;
      const [rName, rSound] = await Promise.all([
        api
          .get(`/practice/alphabet?${q}&mode=name`)
          .catch(() => ({ data: {} })),
        api
          .get(`/practice/alphabet?${q}&mode=sound`)
          .catch(() => ({ data: {} })),
      ]);
      const want = new Set(remoteChars);
      const seen = new Set();
      const picked = [];
      for (const qq of [
        ...(rName.data?.questions || []),
        ...(rSound.data?.questions || []),
      ]) {
        if (!want.has(qq.char)) continue;
        const k = qq.key || `${qq.char}|${qq.prompt}`;
        if (seen.has(k)) continue;
        seen.add(k);
        picked.push(qq);
      }
      if (picked.length)
        parts.push({
          kind: "alphabet",
          title: t("myerrors.lettres_de_base"),
          cards: remoteChars.map((c) => letterByChar[c]).filter(Boolean),
          questions: cleanAlphabet(makeStrictSequence(picked)),
        });
    }
    for (const m of ALPHABET_MODULES) {
      if (m.remote) continue;
      const chars = byMod[m.slug];
      if (!chars?.size) continue;
      const qs = buildModuleQuestions(
        m,
        m.groups || [],
        [],
        "both",
        Math.max(10, chars.size * 4),
        chars,
      );
      if (!qs.length) continue;
      parts.push({
        kind: "alphabet",
        title: m.title,
        cards: [...chars].map((c) => letterByChar[c]).filter(Boolean),
        questions: cleanAlphabet(makeStrictSequence(qs)),
      });
    }
    return parts.length ? parts : null;
  };

  const buildVocabPart = (errs) => {
    const ids = [
      ...new Set(
        errs
          .map(
            (e) =>
              (e.ref && wordById[e.ref] && e.ref) ||
              wordArIndex[String(e.item || "").trim()] ||
              wordArIndex[String(e.correct || "").trim()],
          )
          .filter(Boolean),
      ),
    ];
    const words = ids.map((id) => wordById[id]).filter(Boolean);
    if (!words.length) return null;
    const all = Object.values(wordById);
    const wide = all.length >= 6 ? all : all.concat(all, all);
    const raw = buildVocabQuestions(words, "mixed", wide);
    if (!raw?.length) return null;
    const questions = raw.map((q) => ({
      ...q,
      __eid: q.ref ? `word:${q.ref}` : `key:${asKey(q.key ?? q.id)}`,
    }));
    return {
      kind: "vocab",
      title: t("myerrors.mots"),
      cards: words,
      questions,
    };
  };

  const buildReviewPart = async (errs) => {
    if (!errs.length) return null;
    const { data } = await api
      .get(`/progress/review?scope=errors&limit=1000`)
      .catch(() => ({ data: {} }));
    const all = data?.questions || [];
    const byKey = {};
    const byRef = {};
    for (const e of errs) {
      byKey[asKey(e.key)] = e;
      if (e.ref) byRef[e.ref] = e;
    }
    const picked = [];
    for (const q of all) {
      const e =
        byKey[asKey(q.key)] || byKey[asKey(q.id)] || (q.ref && byRef[q.ref]);
      if (!e) continue;
      picked.push({ ...q, __eid: `key:${asKey(e.key)}`, __kind: e.__kind });
    }
    if (!picked.length) return null;
    return {
      kind: "other",
      title: t("myerrors.questions"),
      cards: [],
      questions: picked,
    };
  };

  const sample = (errs) => {
    if (errs.length <= count) return errs;
    if (order === "tough")
      return [...errs]
        .sort(
          (a, b) =>
            (b.fails || 0) - (a.fails || 0) || (a.box || 0) - (b.box || 0),
        )
        .slice(0, count);
    return shuffle(errs).slice(0, count);
  };

  const buildRoundParts = useCallback((parts, prog) => {
    return parts
      .map((p) => {
        const used = {};
        const qs = shuffle(p.questions)
          .filter((q) => {
            const id = identityOfQuestion(q);
            const box = prog[id]?.box ?? 0;
            const need = MASTER_STREAK - box;
            if (need <= 0) return false;
            used[id] = (used[id] || 0) + 1;
            return used[id] <= need;
          })
          .map(shuffleOptions);
        return { ...p, questions: qs };
      })
      .filter((p) => p.questions.length);
  }, []);

  const makeProgress = useCallback(
    (pool) => {
      const m = {};
      for (const e of pool) {
        const c = classify(e);
        let id;
        let label;
        let ar = null;
        if (c.kind === "alphabet" && c.char) {
          id = `char:${c.char}`;
          ar = c.char;
          label = letterByChar[c.char]?.name || c.char;
        } else if (c.kind === "vocab" && c.wordId) {
          const w = wordById[c.wordId];
          id = `word:${c.wordId}`;
          ar = w?.word_ar || null;
          label = w?.translation_en || e.item || "—";
        } else {
          id = `key:${asKey(e.key)}`;
          label =
            e.item ||
            reviewByKey[asKey(e.key)]?.prompt ||
            prettifyLessonId(e.key);
        }
        const box = Math.max(0, Math.min(MASTER_STREAK, e.box || 0));
        if (!m[id]) m[id] = { id, label, ar, kind: c.kind, box };
        else m[id].box = Math.min(m[id].box, box);
      }
      return m;
    },
    [classify, letterByChar, wordById, reviewByKey],
  );

  const startSession = async (errs, { respectCount = true } = {}) => {
    setNotice("");
    const pool = respectCount ? sample(errs) : errs;
    if (!pool.length) return;
    setStarting(true);
    try {
      const alpha = [];
      const vocab = [];
      const other = [];
      for (const e of pool) {
        const c = classify(e);
        if (c.kind === "alphabet") alpha.push(e);
        else if (c.kind === "vocab" && c.wordId) vocab.push(e);
        else other.push({ ...e, __kind: c.kind });
      }

      const parts = [];
      if (alpha.length) {
        const ps = await buildAlphabetPart(alpha);
        if (ps) parts.push(...ps);
        else other.push(...alpha);
      }
      if (vocab.length) {
        const p = buildVocabPart(vocab);
        if (p) parts.push(p);
        else other.push(...vocab);
      }
      if (other.length) {
        const p = await buildReviewPart(other);
        if (p) parts.push(p);
      }

      if (!parts.length) {
        setNotice(t("myerrors.notice_reconstruction_impossible"));
        return;
      }

      const prog = makeProgress(pool);
      setProgressBoth(prog);

      const roundParts = buildRoundParts(parts, prog);
      if (!roundParts.length) {
        setNotice(t("myerrors.notice_tout_deja_valide"));
        return;
      }

      setDone(null);
      setRoundBreak(null);
      setPlan({
        parts,
        roundParts,
        index: 0,
        roundKey: 1,
        round: 1,
        agg: { correct: 0, total: 0, earned: 0 },
        earnedAtRoundStart: 0,
      });
      setLearnPending(showLearn && roundParts[0].kind !== "other");
    } catch (err) {
      console.error(err);
      setNotice(t("myerrors.notice_seance_echouee"));
    } finally {
      setStarting(false);
    }
  };

  const errorsByKey = useMemo(() => {
    const m = {};
    for (const e of errors || []) m[e.key] = e;
    return m;
  }, [errors]);

  const selectedErrors = useMemo(
    () =>
      Array.from(selectedKeys)
        .map((k) => errorsByKey[k])
        .filter(Boolean),
    [selectedKeys, errorsByKey],
  );

  const askSession = (errs, respectCount) => {
    if (!errs.length) return;
    setPending({ errs, respectCount });
  };

  const playSelection = () => askSession(selectedErrors, false);
  const playGroup = (errs) => askSession(errs, false);
  const playAll = () => askSession(visibleErrors, true);
  const playRandom = () => {
    setOrder("random");
    askSession(visibleErrors, true);
  };

  const confirmStart = () => {
    const p = pending;
    setPending(null);
    if (p) startSession(p.errs, { respectCount: p.respectCount });
  };

  const exitSession = () => {
    setPlan(null);
    setDone(null);
    setRoundBreak(null);
    setLearnPending(false);
    setConfirmExit(false);
    setHint(null);
    setSelectedKeys(new Set());
    setProgressBoth({});
    load();
  };

  const applyLiveAnswer = useCallback(
    (q, good) => {
      const qid = identityOfQuestion(q);

      const prev = progressRef.current;
      const cur = prev[qid];
      if (cur) {
        const box = good
          ? Math.min(MASTER_STREAK, cur.box + 1)
          : Math.max(0, cur.box - 1);
        setProgressBoth({ ...prev, [qid]: { ...cur, box } });
      }

      setErrors((prevErrs) => {
        if (!prevErrs) return prevErrs;
        let touched = false;
        const next = prevErrs.map((e) => {
          if (identityOfError(e) !== qid) return e;
          touched = true;
          const box = good
            ? Math.min(MASTER_STREAK, (e.box || 0) + 1)
            : Math.max(0, (e.box || 0) - 1);
          const fails = good ? e.fails || 0 : (e.fails || 0) + 1;
          return { ...e, box, fails, __mastered: box >= MASTER_STREAK };
        });
        if (!touched) return prevErrs;
        return next.filter((e) => !e.__mastered);
      });
    },
    [identityOfError, setProgressBoth],
  );

  const onPartDone = (result) => {
    setPlan((prev) => {
      if (!prev) return prev;
      const agg = {
        correct: prev.agg.correct + (result.correct || 0),
        total: prev.agg.total + (result.total || 0),
        earned: prev.agg.earned + (result.earned || 0),
      };
      if (agg.earned > prev.agg.earned)
        window.dispatchEvent(new CustomEvent("arabdex:dinar-update"));
      const next = { ...prev, agg };

      if (prev.index + 1 < prev.roundParts.length) {
        const nextPart = prev.roundParts[prev.index + 1];
        setLearnPending(
          showLearn && prev.round === 1 && nextPart.kind !== "other",
        );
        return { ...next, index: prev.index + 1 };
      }

      if (playMode === "infinite") {
        const remaining = buildRoundParts(prev.parts, progressRef.current);
        const left = remaining.reduce((n, p) => n + p.questions.length, 0);
        if (left > 0) {
          setRoundBreak({
            round: prev.round,
            left,
            earnedThisRound: agg.earned - (prev.earnedAtRoundStart || 0),
            earnedTotal: agg.earned,
          });
          return {
            ...next,
            roundParts: remaining,
            index: 0,
            round: prev.round + 1,
            roundKey: prev.roundKey + 1,
            earnedAtRoundStart: agg.earned,
          };
        }
      }
      setDone(agg);
      return next;
    });
  };

  const handleQuizAnswer = (part, q, good) => {
    applyLiveAnswer(q, good);
    if (part.kind === "vocab" && q.ref)
      api
        .post("/progression/vocab/answer", { word_id: q.ref, correct: good })
        .catch(() => {});
  };

  const progressItems = useMemo(() => Object.values(progress), [progress]);
  const masteredCount = progressItems.filter(
    (i) => i.box >= MASTER_STREAK,
  ).length;

  const PageStyles = <style>{ACTIONBAR_STYLES}</style>;

  if (starting) {
    return (
      <div className="flex flex-col items-center justify-center mt-32 text-muted-foreground">
        <Sparkles className="animate-pulse mb-3" size={30} />
        <p className="text-sm font-medium">
          {t("myerrors.preparation_de_ta_seance")}
        </p>
      </div>
    );
  }

  if (done) {
    const score = done.total
      ? Math.round((done.correct / done.total) * 100)
      : 0;
    const passed = score >= 70;
    const tone = toneOf(plan?.roundParts?.[plan.index]?.kind);
    return (
      <SummaryShell
        tone={tone}
        footer={
          <>
            <button
              onClick={exitSession}
              className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold"
            >
              {t("exerciseblock.continuer")}
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setDone(null);
                  setPlan((p) => {
                    if (!p) return p;
                    const rp = buildRoundParts(p.parts, progressRef.current);
                    if (!rp.length) {
                      exitSession();
                      return p;
                    }
                    return {
                      ...p,
                      roundParts: rp,
                      index: 0,
                      round: 1,
                      roundKey: p.roundKey + 1,
                      agg: { correct: 0, total: 0, earned: 0 },
                      earnedAtRoundStart: 0,
                    };
                  });
                }}
                className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
              >
                <RotateCcw size={15} /> {t("myerrors.recommencer")}
              </button>
              <button
                onClick={() => navigate("/practice")}
                className="py-3 rounded-2xl border font-semibold flex items-center justify-center gap-2"
              >
                <ArrowLeft size={15} /> {t("coursequestions.accueil")}
              </button>
            </div>
          </>
        }
      >
        <div className="shrink-0 relative rounded-3xl border bg-card p-4 text-center overflow-hidden">
          {done.earned > 0 && <ConfettiBurst />}
          <PartyPopper size={30} className="mx-auto mb-1.5 text-amber-500" />
          <h2 className="font-head text-xl font-bold leading-tight">
            {t("dailyplansession.seance_terminee")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {done.correct} / {done.total} {t("myerrors.bonnes_reponses")}{" "}
            {score}%
          </p>
          <div className="h-2 rounded-full bg-secondary overflow-hidden my-2.5">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${score}%`,
                backgroundColor: passed ? "#16A34A" : "hsl(var(--destructive))",
              }}
            />
          </div>
          <div className="font-head text-3xl font-extrabold text-amber-500 leading-none">
            +{done.earned || 0} 🪙
          </div>
          <p className="text-[11px] text-muted-foreground mt-1">
            {done.earned > 0
              ? t("myerrors.gagnes_sur_cette_seance_deja_credites")
              : t("quiz.aucun_dinar_cette_fois_retente")}
          </p>
        </div>

        <ProgressList items={progressItems} />

        <p className="shrink-0 text-[11px] text-center text-muted-foreground">
          {masteredCount > 0
            ? t("myerrors.recap_elements_valides", {
                count: masteredCount,
                s: masteredCount > 1 ? "s" : "",
                streak: MASTER_STREAK,
                verb:
                  masteredCount > 1
                    ? t("myerrors.ils_sortent")
                    : t("myerrors.il_sort"),
              })
            : t("myerrors.recap_regle_streak", { streak: MASTER_STREAK })}
        </p>
      </SummaryShell>
    );
  }

  if (plan && roundBreak) {
    const tone = toneOf(plan.roundParts?.[0]?.kind);
    return (
      <>
        <SummaryShell
          tone={tone}
          footer={
            <>
              <button
                onClick={() => setRoundBreak(null)}
                className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2"
              >
                {t("myerrors.tour_suivant")} <ArrowRight size={16} />
              </button>
              <button
                onClick={() => setConfirmExit(true)}
                className="w-full py-3 rounded-2xl border font-semibold"
              >
                {t("myerrors.arreter_la")}
              </button>
            </>
          }
        >
          <div className="shrink-0 rounded-3xl border bg-card p-4 text-center">
            <InfinityIcon size={28} className="mx-auto mb-1.5 text-primary" />
            <h2 className="font-head text-xl font-bold leading-tight">
              {t("myerrors.tour")} {roundBreak.round} {t("myerrors.termine")}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t("myerrors.nb_questions_restantes", {
                count: roundBreak.left,
                s: roundBreak.left > 1 ? "s" : "",
              })}{" "}
              {t("myerrors.encore_a_valider")}
            </p>
            <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-2.5 mt-3">
              <div className="font-head text-2xl font-extrabold text-amber-500 leading-none">
                +{roundBreak.earnedThisRound || 0} 🪙
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {t("myerrors.sur_ce_tour")} {roundBreak.earnedTotal || 0}{" "}
                {t("myerrors.depuis_le_debut")}
              </div>
            </div>
          </div>

          <ProgressList items={progressItems} />
        </SummaryShell>
        {confirmExit && (
          <ConfirmDialog
            title={t("myerrors.arreter_la_seance")}
            message={t("myerrors.validations_dinars_conserves")}
            confirmLabel={t("myerrors.arreter")}
            onCancel={() => setConfirmExit(false)}
            onConfirm={() => {
              setConfirmExit(false);
              setRoundBreak(null);
              setDone(plan.agg);
            }}
          />
        )}
      </>
    );
  }

  if (plan) {
    const part = plan.roundParts[plan.index];

    if (learnPending && part.kind !== "other" && (part.cards || []).length) {
      return (
        <>
          <LearnDeck
            kind={part.kind}
            title={part.title}
            cards={part.cards}
            onAudio={playAudio}
            onDone={() => setLearnPending(false)}
            onExit={() => setConfirmExit(true)}
          />
          {confirmExit && (
            <ConfirmDialog
              title={t("myerrors.quitter_la_seance")}
              message={t("myerrors.validations_conservees")}
              confirmLabel={t("myerrors.quitter")}
              onCancel={() => setConfirmExit(false)}
              onConfirm={exitSession}
            />
          )}
        </>
      );
    }

    const title =
      playMode === "infinite"
        ? `${part.title} · ${t("myerrors.tour")} ${plan.round}`
        : plan.roundParts.length > 1
          ? `${part.title} (${plan.index + 1}/${plan.roundParts.length})`
          : part.title;

    return (
      <>
        {plan.agg.earned > 0 && (
          <div className="fixed top-3 right-3 z-[60] flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 px-3 py-1.5 backdrop-blur-sm">
            <Coins size={14} className="text-amber-500" />
            <span className="text-sm font-bold text-amber-600 tabular-nums">
              +{plan.agg.earned}
            </span>
          </div>
        )}
        <Quiz
          key={`${plan.roundKey}-${plan.index}`}
          questions={part.questions}
          onDone={onPartDone}
          record={true}
          rewardMode="errors"
          rewardAmount={REWARD_ERRORS}
          showCelebration={false}
          title={title}
          onExit={() => setConfirmExit(true)}
          onHint={
            part.kind === "alphabet"
              ? (q) => {
                  const l = letterByChar[q.char];
                  if (l) setHint({ kind: "letter", data: l });
                }
              : part.kind === "vocab"
                ? (q) => {
                    const w = wordById[q.ref] || wordById[q.key];
                    if (w) setHint({ kind: "word", data: w });
                  }
                : null
          }
          onAnswer={(q, good) => handleQuizAnswer(part, q, good)}
        />
        {confirmExit && (
          <ConfirmDialog
            title={t("myerrors.quitter_la_seance")}
            message={t("myerrors.validations_dinars_conserves")}
            confirmLabel={t("myerrors.quitter")}
            onCancel={() => setConfirmExit(false)}
            onConfirm={exitSession}
          />
        )}
        {hint && (
          <div
            className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setHint(null)}
          >
            <div
              className="w-full max-w-sm rounded-3xl border bg-card p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-end mb-1">
                <button
                  onClick={() => setHint(null)}
                  className="rounded-full p-1.5 hover:bg-muted"
                >
                  <X size={17} />
                </button>
              </div>
              {hint.kind === "letter" ? (
                <LetterCard letter={hint.data} onAudio={playAudio} />
              ) : (
                <WordCard word={hint.data} />
              )}
              <button
                onClick={() => setHint(null)}
                className="w-full mt-5 py-3 rounded-2xl bg-primary text-primary-foreground font-bold"
              >
                {t("exerciseblock.j_ai_compris")}
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (errors === null) {
    return (
      <div className="text-center text-sm text-muted-foreground mt-20">
        {t("myerrors.charger_de_tes_erreurs")}
      </div>
    );
  }

  if (counts.all === 0) {
    return (
      <div className="max-w-md mx-auto px-4 mt-20 text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-500/10 grid place-items-center mx-auto mb-4">
          <Target size={36} className="text-emerald-500" />
        </div>
        <h1 className="font-head text-2xl font-bold mb-2">
          {t("myerrors.zero_erreur")}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          {t("myerrors.tout_ce_que_tu_as_rate")} {MASTER_STREAK}{" "}
          {t("myerrors.fois_continue_a_t_entrainer_pour")}
        </p>
        <button
          onClick={() => navigate("/practice")}
          className="w-full py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold"
        >
          {t("myerrors.aller_m_entrainer")}
        </button>
      </div>
    );
  }

  const totalVisible = visibleErrors.length;
  const willPlay = Math.min(count, totalVisible);

  return (
    <>
      {PageStyles}
      <div className="prac-page max-w-2xl mx-auto px-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h1 className="font-head text-2xl font-bold">
              {t("myerrors.mes_erreurs")}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t("myerrors.valide_en_une")} {MASTER_STREAK}{" "}
              {t("myerrors.fois_de_suite_pour_la_faire")}
            </p>
          </div>
          <button
            onClick={playRandom}
            disabled={totalVisible === 0}
            className="shrink-0 flex items-center gap-1.5 rounded-full border-2 border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 transition disabled:opacity-40"
          >
            <Shuffle size={13} /> {t("myerrors.aleatoire")}
          </button>
        </div>

        <ErrorsHero
          total={counts.all}
          tough={counts.tough}
          kinds={kindsBreakdown}
        />

        <FilterChips filter={filter} setFilter={setFilter} counts={counts} />

        {notice && (
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3 mb-3 text-sm text-amber-800 dark:text-amber-300">
            {notice}
          </div>
        )}

        {sections.length === 0 ? (
          <div className="rounded-2xl border border-dashed bg-card/50 p-8 text-center">
            <ListChecks
              size={30}
              className="mx-auto mb-3 text-muted-foreground opacity-50"
            />
            <h3 className="font-head text-base font-bold mb-1">
              {t("myerrors.rien_dans_ce_filtre")}
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              {t("myerrors.aucune_erreur_ne_correspond_a_ce")}
            </p>
            <button
              onClick={() => setFilter("all")}
              className="text-sm text-primary font-semibold"
            >
              {t("myerrors.voir_tout")}
            </button>
          </div>
        ) : (
          sections.map((section) => {
            const meta = KIND_CONFIG[section.kind];
            const Icon = meta.icon;
            const allErrorsInSection = section.categories
              ? section.categories.flatMap((cat) =>
                  cat.subs.flatMap((s) => s.errors),
                )
              : section.groups.flatMap((g) => g.errors);
            const sectionKeys = allErrorsInSection.map((e) => e.key);
            const sectionOpen = !!openMap[section.id];

            return (
              <div key={section.id} className="mb-2.5">
                <TreeCard
                  title={
                    <span className="flex items-center gap-1.5">
                      <Icon size={14} style={{ color: meta.color }} />
                      {section.title}
                    </span>
                  }
                  metaBadge={t("myerrors.nb_erreurs_court", {
                    count: sectionKeys.length,
                  })}
                  depth={0}
                  tone={meta}
                  open={sectionOpen}
                  onToggleOpen={() => toggleOpen(section.id)}
                  checkboxState={checkboxStateFor(sectionKeys, selectedKeys)}
                  onCheck={() =>
                    toggleKeys(
                      sectionKeys,
                      checkboxStateFor(sectionKeys, selectedKeys) !== "all",
                    )
                  }
                  onPlay={() => playGroup(allErrorsInSection)}
                >
                  {section.categories
                    ? section.categories.map((cat) => {
                        const catKeys = cat.subs.flatMap((s) =>
                          s.errors.map((e) => e.key),
                        );
                        const catOpen = !!openMap[cat.id];
                        return (
                          <TreeCard
                            key={cat.id}
                            title={cat.title}
                            metaBadge={t("myerrors.nb_sous_categories", {
                              count: cat.subs.length,
                            })}
                            depth={1}
                            open={catOpen}
                            onToggleOpen={() => toggleOpen(cat.id)}
                            checkboxState={checkboxStateFor(
                              catKeys,
                              selectedKeys,
                            )}
                            onCheck={() =>
                              toggleKeys(
                                catKeys,
                                checkboxStateFor(catKeys, selectedKeys) !==
                                  "all",
                              )
                            }
                            onPlay={() =>
                              playGroup(cat.subs.flatMap((s) => s.errors))
                            }
                          >
                            <div className="space-y-2">
                              {cat.subs.map((sub) => {
                                const subKeys = sub.errors.map((e) => e.key);
                                const subOpen = !!openMap[sub.id];
                                const totalChars = sub.allChars?.length;
                                const pct =
                                  section.kind === "vocab"
                                    ? sub.pct
                                    : totalChars
                                      ? Math.round(
                                          (Math.max(
                                            0,
                                            totalChars - subKeys.length,
                                          ) /
                                            totalChars) *
                                            100,
                                        )
                                      : null;
                                const caption =
                                  section.kind === "vocab" && sub.pct != null
                                    ? t("myerrors.nb_erreurs_court", {
                                        count: sub.errors.length,
                                      })
                                    : totalChars
                                      ? t("myerrors.nb_lettres_maitrisees", {
                                          count: Math.max(
                                            0,
                                            totalChars - subKeys.length,
                                          ),
                                          total: totalChars,
                                        })
                                      : null;
                                return (
                                  <TreeCard
                                    key={sub.id}
                                    title={sub.title}
                                    metaBadge={t("myerrors.nb_erreurs_court", {
                                      count: sub.errors.length,
                                    })}
                                    progressPct={pct}
                                    progressCaption={caption}
                                    depth={2}
                                    open={subOpen}
                                    onToggleOpen={() => toggleOpen(sub.id)}
                                    checkboxState={checkboxStateFor(
                                      subKeys,
                                      selectedKeys,
                                    )}
                                    onCheck={() =>
                                      toggleKeys(
                                        subKeys,
                                        checkboxStateFor(
                                          subKeys,
                                          selectedKeys,
                                        ) !== "all",
                                      )
                                    }
                                    onPlay={() => playGroup(sub.errors)}
                                  >
                                    {section.kind === "alphabet" ? (
                                      <AlphabetGroupBody
                                        errors={sub.errors}
                                        selectedKeys={selectedKeys}
                                        onToggleKeys={toggleKeys}
                                        onAudio={playAudio}
                                        letterByChar={letterByChar}
                                      />
                                    ) : section.kind === "vocab" ? (
                                      <VocabGroupBody
                                        errors={sub.errors}
                                        selectedKeys={selectedKeys}
                                        onToggleKeys={toggleKeys}
                                        wordById={wordById}
                                        wordArIndex={wordArIndex}
                                        onDeleteOne={deleteOne}
                                      />
                                    ) : (
                                      <SimpleGroupBody
                                        errors={sub.errors}
                                        selectedKeys={selectedKeys}
                                        onToggleKeys={toggleKeys}
                                        onDeleteOne={deleteOne}
                                        reviewByKey={reviewByKey}
                                      />
                                    )}
                                  </TreeCard>
                                );
                              })}
                            </div>
                          </TreeCard>
                        );
                      })
                    : section.groups.map((g) => {
                        const gKeys = g.errors.map((e) => e.key);
                        const gOpen = !!openMap[g.id];
                        return (
                          <TreeCard
                            key={g.id}
                            title={g.title}
                            metaBadge={t("myerrors.nb_erreurs_court", {
                              count: g.errors.length,
                            })}
                            depth={1}
                            open={gOpen}
                            onToggleOpen={() => toggleOpen(g.id)}
                            checkboxState={checkboxStateFor(
                              gKeys,
                              selectedKeys,
                            )}
                            onCheck={() =>
                              toggleKeys(
                                gKeys,
                                checkboxStateFor(gKeys, selectedKeys) !== "all",
                              )
                            }
                            onPlay={() => playGroup(g.errors)}
                          >
                            <SimpleGroupBody
                              errors={g.errors}
                              selectedKeys={selectedKeys}
                              onToggleKeys={toggleKeys}
                              onDeleteOne={deleteOne}
                              reviewByKey={reviewByKey}
                            />
                          </TreeCard>
                        );
                      })}
                </TreeCard>
              </div>
            );
          })
        )}

        <button
          onClick={() => setShowHelp((s) => !s)}
          className="w-full rounded-2xl border bg-card px-3 py-2.5 flex items-center justify-between gap-2 mt-6 text-left"
        >
          <span className="text-xs font-semibold">
            {t("myerrors.comment_ca_marche")}
          </span>
          <ChevronDown
            size={15}
            className={`shrink-0 transition-transform ${showHelp ? "rotate-180" : ""}`}
          />
        </button>
        {showHelp && (
          <div className="rounded-2xl border bg-card p-3 mt-2 text-xs text-muted-foreground leading-relaxed space-y-1.5">
            <p>{t("myerrors.des_que_tu_te_trompes_la")}</p>
            <p>
              {t("myerrors.les_trois_points_de_chaque_ligne")} {MASTER_STREAK}/
              {MASTER_STREAK}
              {t("myerrors.l_erreur_est_maitrisee_lettre_mot")}
            </p>
            <p>
              {t("myerrors.la_flamme_signale_une_erreur_ratee")} {TOUGH_AT}{" "}
              {t("myerrors.fois_ou_plus_le_mode")}{" "}
              <strong>{t("myerrors.mode_infini")}</strong>{" "}
              {t("myerrors.fait_tourner_tes_erreurs_en_boucle")}
            </p>
          </div>
        )}
      </div>

      <div className="prac-actionbar border-t">
        <div className="max-w-2xl mx-auto flex items-center gap-2">
          {selectedKeys.size > 0 && (
            <button
              onClick={() => setSelectedKeys(new Set())}
              className="shrink-0 px-4 py-3.5 rounded-2xl border text-sm font-semibold"
            >
              {t("myerrors.vider")}
            </button>
          )}
          <button
            disabled={totalVisible === 0}
            onClick={selectedKeys.size > 0 ? playSelection : playAll}
            className="flex-1 py-3.5 rounded-2xl bg-primary text-primary-foreground font-bold disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {playMode === "infinite" ? (
              <InfinityIcon size={17} />
            ) : (
              <Play size={16} fill="currentColor" />
            )}
            {selectedKeys.size > 0
              ? t("myerrors.jouer_nb_erreurs", {
                  count: selectedKeys.size,
                  s: selectedKeys.size > 1 ? "s" : "",
                })
              : t("myerrors.reviser_nb_erreurs", {
                  count: willPlay,
                  s: willPlay > 1 ? "s" : "",
                })}
          </button>
        </div>
      </div>

      {pending && (
        <LaunchSheet
          pending={pending}
          playMode={playMode}
          setPlayMode={setPlayMode}
          count={count}
          setCount={setCount}
          order={order}
          setOrder={setOrder}
          showLearn={showLearn}
          setShowLearn={setShowLearn}
          onStart={confirmStart}
          onClose={() => setPending(null)}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}
